import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Toast from '../components/Toast';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1', sky: '#38BDF8',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const inputStyle = {
  width: '100%',
  padding: '9px 13px',
  background: 'rgba(255,255,255,0.04)',
  border: `1.5px solid ${C.border}`,
  borderRadius: 8,
  fontSize: '0.875rem',
  color: C.text,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtnStyle = { background: C.gold, color: C.ink, border: 'none', fontWeight: 700, fontFamily: 'inherit' };

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const TABS = ['API Keys', 'Webhooks', 'API Reference'];
const EVENTS = ['sale.created', 'customer.created', 'stock.low', 'stock.out'];

function tag(text, color) {
  const colors = {
    green:  { bg: 'rgba(0,201,177,0.15)',  fg: C.teal },
    red:    { bg: 'rgba(255,107,107,0.15)', fg: C.coral },
    blue:   { bg: 'rgba(56,189,248,0.15)',  fg: C.sky },
    yellow: { bg: 'rgba(240,180,41,0.15)',  fg: C.gold },
    gray:   { bg: 'rgba(255,255,255,0.06)', fg: C.muted },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.fg }}>
      {text}
    </span>
  );
}

// ── API Keys Tab ──────────────────────────────────────────────────────────────
function ApiKeysTab({ token }) {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () =>
    fetch(`${API}/api/v1/api-keys`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setKeys);

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    const res = await fetch(`${API}/api/v1/api-keys`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const data = await res.json();
    setCreating(false);
    if (!res.ok) return setToast({ message: data.error, type: 'error' });
    setNewKey(data.key);
    setName('');
    load();
  };

  const revoke = async (id) => {
    if (!confirm('Revoke this API key? It cannot be undone.')) return;
    await fetch(`${API}/api/v1/api-keys/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setToast({ message: 'API key revoked', type: 'success' });
    load();
  };

  const copy = (text) => { navigator.clipboard.writeText(text); setToast({ message: 'Copied!', type: 'success' }); };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {newKey && (
        <div style={{ background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.3)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <p style={{ fontSize: '0.85rem', fontWeight: 700, color: C.teal, marginBottom: 8 }}>
            ✅ API key created — save it now. It won't be shown again.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <code style={{ fontSize: '0.8rem', background: 'rgba(0,201,177,0.1)', padding: '6px 12px', borderRadius: 6, flex: 1, wordBreak: 'break-all', color: C.teal }}>
              {newKey}
            </code>
            <button className="btn btn-sm" onClick={() => copy(newKey)} style={{ ...primaryBtnStyle, whiteSpace: 'nowrap' }}>Copy</button>
          </div>
          <button onClick={() => setNewKey(null)} style={{ marginTop: 8, fontSize: '0.75rem', background: 'none', border: 'none', color: C.teal, cursor: 'pointer', fontFamily: 'inherit' }}>Dismiss</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <input
          placeholder="Key name, e.g. My Integration"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && create()}
          style={{ ...inputStyle, flex: 1 }}
        />
        <button onClick={create} disabled={creating || !name.trim()} style={{ ...primaryBtnStyle, padding: '9px 18px', borderRadius: 10, cursor: creating || !name.trim() ? 'not-allowed' : 'pointer', opacity: creating || !name.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}>
          {creating ? 'Creating…' : '+ Create Key'}
        </button>
      </div>

      {keys.length === 0 ? (
        <p style={{ color: C.muted, fontSize: '0.9rem' }}>No API keys yet. Create one above to get started.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {keys.map((k) => (
            <div key={k.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>{k.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{ fontSize: '0.75rem', color: C.muted }}>{k.keyPrefix}…</code>
                  <button onClick={() => copy(k.keyPrefix + '…')} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit' }}>copy prefix</button>
                </div>
                <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 4 }}>
                  Created {new Date(k.createdAt).toLocaleDateString()}
                  {k.lastUsed && ` · Last used ${new Date(k.lastUsed).toLocaleDateString()}`}
                </div>
              </div>
              <button onClick={() => revoke(k.id)} style={{ background: 'rgba(255,107,107,0.12)', color: C.coral, border: 'none', flexShrink: 0, padding: '4px 10px', borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 28, padding: 16, background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: '0.8rem', color: C.muted }}>
        <p style={{ fontWeight: 700, color: C.sub, marginBottom: 8 }}>Usage</p>
        <code style={{ color: C.sky, display: 'block', marginBottom: 6 }}>
          Authorization: Bearer bzt_live_...
        </code>
        <p>Include this header in every API request. Keep your key secret — it has full access to your account.</p>
      </div>
    </div>
  );
}

// ── Webhooks Tab ──────────────────────────────────────────────────────────────
function WebhooksTab({ token }) {
  const [webhooks, setWebhooks] = useState([]);
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newWebhook, setNewWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState({});
  const [toast, setToast] = useState(null);

  const load = () =>
    fetch(`${API}/api/v1/webhooks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setWebhooks);

  useEffect(() => { load(); }, []);

  const toggleEvent = (e) =>
    setSelectedEvents((prev) => prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]);

  const create = async () => {
    if (!url || !selectedEvents.length) return;
    setCreating(true);
    const res = await fetch(`${API}/api/v1/webhooks`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, events: selectedEvents }),
    });
    const data = await res.json();
    setCreating(false);
    if (!res.ok) return setToast({ message: data.error, type: 'error' });
    setNewWebhook(data);
    setUrl(''); setSelectedEvents([]);
    load();
  };

  const toggle = async (id, active) => {
    await fetch(`${API}/api/v1/webhooks/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !active }),
    });
    load();
  };

  const remove = async (id) => {
    if (!confirm('Delete this webhook?')) return;
    await fetch(`${API}/api/v1/webhooks/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setToast({ message: 'Webhook deleted', type: 'success' });
    load();
  };

  const loadDeliveries = async (id) => {
    if (deliveries[id]) return setDeliveries((d) => ({ ...d, [id]: null }));
    const res = await fetch(`${API}/api/v1/webhooks/${id}/deliveries`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setDeliveries((d) => ({ ...d, [id]: data }));
  };

  const copy = (text) => { navigator.clipboard.writeText(text); setToast({ message: 'Copied!', type: 'success' }); };

  const smBtn = { background: 'rgba(255,255,255,0.06)', color: C.sub, border: 'none', fontSize: '0.75rem', padding: '4px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {newWebhook && (
        <div style={{ background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.3)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <p style={{ fontSize: '0.85rem', fontWeight: 700, color: C.teal, marginBottom: 8 }}>
            ✅ Webhook created — save the signing secret now. It won't be shown again.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <code style={{ fontSize: '0.75rem', background: 'rgba(0,201,177,0.1)', padding: '6px 12px', borderRadius: 6, flex: 1, wordBreak: 'break-all', color: C.teal }}>
              {newWebhook.secret}
            </code>
            <button onClick={() => copy(newWebhook.secret)} style={primaryBtnStyle}>Copy</button>
          </div>
          <button onClick={() => setNewWebhook(null)} style={{ marginTop: 8, fontSize: '0.75rem', background: 'none', border: 'none', color: C.teal, cursor: 'pointer', fontFamily: 'inherit' }}>Dismiss</button>
        </div>
      )}

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 24 }}>
        <p style={{ fontWeight: 600, color: C.sub, marginBottom: 12, fontSize: '0.9rem' }}>Register endpoint</p>
        <input
          placeholder="https://myapp.com/webhooks/optika"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          style={{ ...inputStyle, marginBottom: 12 }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {EVENTS.map((e) => (
            <button
              key={e}
              onClick={() => toggleEvent(e)}
              style={{
                padding: '5px 12px',
                borderRadius: 20,
                border: '1px solid',
                fontSize: '0.78rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
                background: selectedEvents.includes(e) ? C.gold : 'rgba(255,255,255,0.05)',
                borderColor: selectedEvents.includes(e) ? C.gold : C.border,
                color: selectedEvents.includes(e) ? C.ink : C.muted,
              }}
            >
              {e}
            </button>
          ))}
        </div>
        <button onClick={create} disabled={creating || !url || !selectedEvents.length} style={{ ...primaryBtnStyle, padding: '9px 18px', borderRadius: 10, cursor: creating || !url || !selectedEvents.length ? 'not-allowed' : 'pointer', opacity: creating || !url || !selectedEvents.length ? 0.6 : 1 }}>
          {creating ? 'Creating…' : '+ Add Webhook'}
        </button>
      </div>

      {webhooks.length === 0 ? (
        <p style={{ color: C.muted, fontSize: '0.9rem' }}>No webhooks registered yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {webhooks.map((wh) => (
            <div key={wh.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {tag(wh.active ? 'Active' : 'Paused', wh.active ? 'green' : 'gray')}
                    {tag(`${wh._count?.deliveries ?? 0} deliveries`, 'blue')}
                  </div>
                  <code style={{ fontSize: '0.78rem', color: C.muted, wordBreak: 'break-all', display: 'block', marginBottom: 6 }}>{wh.url}</code>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {wh.events.map((e) => <span key={e} style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.06)', color: C.muted, padding: '2px 7px', borderRadius: 4 }}>{e}</span>)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => toggle(wh.id, wh.active)} style={smBtn}>
                    {wh.active ? 'Pause' : 'Resume'}
                  </button>
                  <button onClick={() => loadDeliveries(wh.id)} style={smBtn}>
                    Logs
                  </button>
                  <button onClick={() => remove(wh.id)} style={{ ...smBtn, background: 'rgba(255,107,107,0.12)', color: C.coral }}>
                    Delete
                  </button>
                </div>
              </div>

              {deliveries[wh.id] && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                  {deliveries[wh.id].length === 0 ? (
                    <p style={{ fontSize: '0.8rem', color: C.muted }}>No deliveries yet.</p>
                  ) : (
                    <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: C.muted, textAlign: 'left' }}>
                          <th style={{ padding: '4px 8px' }}>Event</th>
                          <th style={{ padding: '4px 8px' }}>Status</th>
                          <th style={{ padding: '4px 8px' }}>Attempts</th>
                          <th style={{ padding: '4px 8px' }}>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries[wh.id].map((d) => (
                          <tr key={d.id} style={{ borderTop: `1px solid ${C.border}` }}>
                            <td style={{ padding: '4px 8px', color: C.muted }}>{d.event}</td>
                            <td style={{ padding: '4px 8px' }}>{tag(d.success ? `${d.statusCode} OK` : `${d.statusCode ?? 'ERR'} Failed`, d.success ? 'green' : 'red')}</td>
                            <td style={{ padding: '4px 8px', color: C.muted }}>{d.attempts}</td>
                            <td style={{ padding: '4px 8px', color: C.muted }}>{new Date(d.createdAt).toLocaleTimeString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 28, padding: 16, background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: '0.8rem', color: C.muted }}>
        <p style={{ fontWeight: 700, color: C.sub, marginBottom: 8 }}>Verifying signatures</p>
        <p style={{ marginBottom: 8 }}>Each request includes an <code style={{ color: C.sky }}>X-OptiKa-Signature</code> header. Verify it with:</p>
        <code style={{ display: 'block', color: C.teal, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{`const sig = req.headers['x-optika-signature'];
const expected = 'sha256=' + crypto
  .createHmac('sha256', YOUR_WEBHOOK_SECRET)
  .update(rawBody)
  .digest('hex');
if (sig !== expected) return res.status(400).send('Invalid signature');`}</code>
      </div>
    </div>
  );
}

// ── API Reference Tab ─────────────────────────────────────────────────────────
function ApiReferenceTab() {
  const docsUrl = `${API}/api/docs`;
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <p style={{ color: C.muted, marginBottom: 12, fontSize: '0.9rem' }}>
          Full interactive API documentation with request/response schemas and a live try-it console.
        </p>
        <a href={docsUrl} target="_blank" rel="noreferrer" style={{ ...primaryBtnStyle, display: 'inline-block', textDecoration: 'none', padding: '9px 18px', borderRadius: 10 }}>
          Open API Reference ↗
        </a>
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, fontSize: '0.82rem', color: C.muted }}>
        <p style={{ fontWeight: 700, color: C.sub, marginBottom: 12 }}>Quick reference</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: 'left', borderBottom: `1px solid ${C.border}` }}>
              <th style={{ padding: '6px 8px' }}>Base URL</th>
              <th style={{ padding: '6px 8px' }}>Rate limit</th>
              <th style={{ padding: '6px 8px' }}>Auth</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['/api/v1', '1,000 req/hr (Trial)', 'Bearer bzt_live_...'],
              ['/api/v1', '1,000 req/hr (Pro)', 'Bearer bzt_live_...'],
              ['/api/v1', '10,000 req/hr (Business)', 'Bearer bzt_live_...'],
            ].map(([base, limit, auth], i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '6px 8px' }}><code style={{ color: C.sky }}>{base}</code></td>
                <td style={{ padding: '6px 8px', color: C.sub }}>{limit}</td>
                <td style={{ padding: '6px 8px' }}><code style={{ color: C.teal }}>{auth}</code></td>
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ fontWeight: 700, color: C.sub, marginTop: 16, marginBottom: 8 }}>Webhook events</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {[
            ['sale.created', 'Fired when a sale is recorded'],
            ['customer.created', 'Fired when a customer is added'],
            ['stock.low', 'Fired when stock drops to ≤5 units'],
            ['stock.out', 'Fired when an item reaches 0'],
          ].map(([event, desc]) => (
            <div key={event} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', minWidth: 200 }}>
              <code style={{ color: C.gold, fontSize: '0.75rem', display: 'block', marginBottom: 2 }}>{event}</code>
              <span style={{ fontSize: '0.72rem', color: C.muted }}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Developer() {
  const [tab, setTab] = useState(0);
  const token = localStorage.getItem('token');

  return (
    <Layout>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 32px' }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: C.text, margin: 0 }}>Developer Platform</h2>
          <p style={{ color: C.muted, marginTop: 4, fontSize: '0.9rem' }}>
            Integrate OptiKa into your workflows with API keys, webhooks, and the REST API.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              style={{
                padding: '8px 18px',
                background: 'none',
                border: 'none',
                borderBottom: tab === i ? `2px solid ${C.gold}` : '2px solid transparent',
                color: tab === i ? C.text : C.muted,
                fontWeight: tab === i ? 700 : 400,
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginBottom: -1,
                fontFamily: 'inherit',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 0 && <ApiKeysTab token={token} />}
        {tab === 1 && <WebhooksTab token={token} />}
        {tab === 2 && <ApiReferenceTab />}
      </div>
    </Layout>
  );
}
